import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import { useRequirementsStore } from '@/views/Requirements/store'
import type { Sprint, SprintStatus, DevelopmentStatus } from '@/types'
import { useProjectsStore } from '@/views/Projects/store'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import type { BacklogState, BacklogFilters } from './state'

export interface CreateSprintData {
  name: string
  goal?: string
  startDate?: Date
  endDate?: Date
}

export interface UpdateSprintData {
  name?: string
  goal?: string
  startDate?: Date
  endDate?: Date
}

export const createActions = (state: BacklogState) => {
  const { logCreate, logUpdate, logStatusChange } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const mapSprintDoc = (d: { id: string; data: () => Record<string, unknown> }): Sprint => {
    const data = d.data()
    return {
      id: d.id,
      tenantId: data.tenantId,
      name: data.name,
      goal: data.goal,
      status: data.status,
      startDate: (data.startDate as any)?.toDate?.(),
      endDate: (data.endDate as any)?.toDate?.(),
      completedAt: (data.completedAt as any)?.toDate?.(),
      requirementIds: data.requirementIds || [],
      totalStoryPoints: data.totalStoryPoints || 0,
      completedStoryPoints: data.completedStoryPoints || 0,
      totalRequirements: data.totalRequirements || 0,
      completedRequirements: data.completedRequirements || 0,
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
      createdBy: data.createdBy,
      updatedBy: data.updatedBy,
    } as Sprint
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isSprintLoading = true
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []
    const q = query(collection(db, 'sprints'), ...baseConstraints, orderBy('createdAt', 'desc'))
    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      let sprints = snapshot.docs.map(mapSprintDoc)
      // Client-side filter for operators
      const authStore = useAuthStore()
      if (authStore.user?.role === 'operator') {
        const projectIds = new Set(useProjectsStore().projects.map((p) => p.id))
        const reqStore = useRequirementsStore()
        const operatorReqIds = new Set(
          reqStore.requirements.filter((r) => projectIds.has(r.projectId)).map((r) => r.id)
        )
        sprints = sprints.filter((s) => s.requirementIds.some((id) => operatorReqIds.has(id)))
      }
      state.sprints = sprints
      state.isSprintLoading = false
    }, (error) => {
      console.error('Sprints subscription error:', error)
      state.isSprintLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchSprints = async (): Promise<void> => {
    state.isSprintLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      const q = query(collection(db, 'sprints'), ...baseConstraints, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      let allSprints = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          tenantId: data.tenantId,
          name: data.name,
          goal: data.goal,
          status: data.status,
          startDate: data.startDate?.toDate?.(),
          endDate: data.endDate?.toDate?.(),
          completedAt: data.completedAt?.toDate?.(),
          requirementIds: data.requirementIds || [],
          totalStoryPoints: data.totalStoryPoints || 0,
          completedStoryPoints: data.completedStoryPoints || 0,
          totalRequirements: data.totalRequirements || 0,
          completedRequirements: data.completedRequirements || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          updatedBy: data.updatedBy,
        } as Sprint
      })

      // For operators: filter sprints client-side by their projects' requirements
      if (authStore.user?.role === 'operator') {
        const projectsStore = useProjectsStore()
        if (projectsStore.projects.length === 0) await projectsStore.fetchProjects()
        const operatorProjectIds = new Set(projectsStore.projects.map(p => p.id))

        const requirementsStore = useRequirementsStore()
        const operatorReqIds = new Set(
          requirementsStore.requirements
            .filter(r => operatorProjectIds.has(r.projectId))
            .map(r => r.id)
        )

        allSprints = allSprints.filter(sprint =>
          sprint.requirementIds.some(reqId => operatorReqIds.has(reqId))
        )
      }

      state.sprints = allSprints
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar sprints'
      throw error
    } finally {
      state.isSprintLoading = false
    }
  }

  const createSprint = async (data: CreateSprintData): Promise<Sprint> => {
    state.isSprintLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      const firestoreData: Record<string, unknown> = {
        ...getTenantData(),
        name: data.name,
        status: 'planning' as SprintStatus,
        requirementIds: [],
        totalStoryPoints: 0,
        completedStoryPoints: 0,
        totalRequirements: 0,
        completedRequirements: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      if (data.goal) firestoreData.goal = data.goal
      if (data.startDate) firestoreData.startDate = data.startDate
      if (data.endDate) firestoreData.endDate = data.endDate

      const docRef = await addDoc(collection(db, 'sprints'), firestoreData)

      const newSprint: Sprint = {
        id: docRef.id,
        name: data.name,
        goal: data.goal,
        status: 'planning',
        startDate: data.startDate,
        endDate: data.endDate,
        requirementIds: [],
        totalStoryPoints: 0,
        completedStoryPoints: 0,
        totalRequirements: 0,
        completedRequirements: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      state.sprints.unshift(newSprint)
      await logCreate('sprint', newSprint.id, newSprint.name)

      await notificationsStore.notifyAdminsAndOperators(
        'sprint_created',
        'Nuevo sprint creado',
        `Se ha creado el sprint "${newSprint.name}"`,
        'sprint',
        newSprint.id
      )

      return newSprint
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al crear sprint'
      throw error
    } finally {
      state.isSprintLoading = false
    }
  }

  const updateSprint = async (sprintId: string, data: UpdateSprintData): Promise<void> => {
    state.isSprintLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      }

      if (data.name !== undefined) updateData.name = data.name
      if (data.goal !== undefined) updateData.goal = data.goal
      if (data.startDate !== undefined) updateData.startDate = data.startDate
      if (data.endDate !== undefined) updateData.endDate = data.endDate

      await updateDoc(doc(db, 'sprints', sprintId), updateData)

      const index = state.sprints.findIndex((s) => s.id === sprintId)
      if (index !== -1) {
        state.sprints[index] = {
          ...state.sprints[index],
          ...data,
          updatedAt: new Date(),
          updatedBy: currentUserId,
        }
      }

      await logUpdate('sprint', sprintId, [], state.sprints[index]?.name)
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al actualizar sprint'
      throw error
    } finally {
      state.isSprintLoading = false
    }
  }

  const startSprint = async (sprintId: string): Promise<void> => {
    // Validate no other active sprint
    const activeSprint = state.sprints.find((s) => s.status === 'active')
    if (activeSprint) {
      throw new Error('Ya existe un sprint activo. Completa o cancela el sprint actual antes de iniciar otro.')
    }

    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) throw new Error('Sprint no encontrado')

    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'

    await updateDoc(doc(db, 'sprints', sprintId), {
      status: 'active',
      startDate: sprint.startDate || serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    const index = state.sprints.findIndex((s) => s.id === sprintId)
    if (index !== -1) {
      state.sprints[index] = {
        ...state.sprints[index],
        status: 'active',
        startDate: state.sprints[index].startDate || new Date(),
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }
    }

    await logStatusChange('sprint', sprintId, 'planning', 'active', sprint.name)

    await notificationsStore.notifyAdminsAndOperators(
      'sprint_started',
      'Sprint iniciado',
      `El sprint "${sprint.name}" ha comenzado`,
      'sprint',
      sprintId
    )
  }

  const completeSprint = async (sprintId: string): Promise<void> => {
    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) throw new Error('Sprint no encontrado')

    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'
    const requirementsStore = useRequirementsStore()

    // Get requirements in this sprint that are NOT in production - clear their sprintId
    const incompleteReqIds = sprint.requirementIds.filter((reqId) => {
      const req = requirementsStore.getRequirementById(reqId)
      return req && req.developmentStatus !== 'production'
    })

    // Batch update: clear sprintId from incomplete requirements
    if (incompleteReqIds.length > 0) {
      const batch = writeBatch(db)
      incompleteReqIds.forEach((reqId) => {
        batch.update(doc(db, 'requirements', reqId), {
          sprintId: null,
          updatedAt: serverTimestamp(),
          updatedBy: currentUserId,
        })
      })
      await batch.commit()

      // Update local state
      incompleteReqIds.forEach((reqId) => {
        const req = requirementsStore.requirements.find((r) => r.id === reqId)
        if (req) {
          req.sprintId = undefined
        }
      })
    }

    // Recalculate final metrics
    const metrics = calculateSprintMetrics(sprintId)

    await updateDoc(doc(db, 'sprints', sprintId), {
      status: 'completed',
      completedAt: serverTimestamp(),
      requirementIds: sprint.requirementIds.filter((id) => !incompleteReqIds.includes(id)),
      ...metrics,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    const index = state.sprints.findIndex((s) => s.id === sprintId)
    if (index !== -1) {
      state.sprints[index] = {
        ...state.sprints[index],
        status: 'completed',
        completedAt: new Date(),
        requirementIds: sprint.requirementIds.filter((id) => !incompleteReqIds.includes(id)),
        ...metrics,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }
    }

    await logStatusChange('sprint', sprintId, 'active', 'completed', sprint.name)

    await notificationsStore.notifyAdminsAndOperators(
      'sprint_completed',
      'Sprint completado',
      `El sprint "${sprint.name}" ha finalizado — ${metrics.completedRequirements}/${metrics.totalRequirements} requerimientos completados`,
      'sprint',
      sprintId
    )
  }

  const cancelSprint = async (sprintId: string): Promise<void> => {
    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) throw new Error('Sprint no encontrado')

    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'
    const requirementsStore = useRequirementsStore()

    // Clear sprintId from ALL requirements in this sprint
    if (sprint.requirementIds.length > 0) {
      const batch = writeBatch(db)
      sprint.requirementIds.forEach((reqId) => {
        batch.update(doc(db, 'requirements', reqId), {
          sprintId: null,
          updatedAt: serverTimestamp(),
          updatedBy: currentUserId,
        })
      })
      await batch.commit()

      // Update local state
      sprint.requirementIds.forEach((reqId) => {
        const req = requirementsStore.requirements.find((r) => r.id === reqId)
        if (req) {
          req.sprintId = undefined
        }
      })
    }

    await updateDoc(doc(db, 'sprints', sprintId), {
      status: 'cancelled',
      requirementIds: [],
      totalStoryPoints: 0,
      completedStoryPoints: 0,
      totalRequirements: 0,
      completedRequirements: 0,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    const index = state.sprints.findIndex((s) => s.id === sprintId)
    if (index !== -1) {
      state.sprints[index] = {
        ...state.sprints[index],
        status: 'cancelled',
        requirementIds: [],
        totalStoryPoints: 0,
        completedStoryPoints: 0,
        totalRequirements: 0,
        completedRequirements: 0,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }
    }

    await logStatusChange('sprint', sprintId, sprint.status, 'cancelled', sprint.name)
  }

  const assignRequirements = async (reqIds: string[], sprintId: string): Promise<void> => {
    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) throw new Error('Sprint no encontrado')
    if (sprint.status !== 'planning' && sprint.status !== 'active') {
      throw new Error('Solo se pueden asignar requerimientos a sprints en planificacion o activos')
    }

    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'
    const requirementsStore = useRequirementsStore()

    // Filter out already assigned
    const newReqIds = reqIds.filter((id) => !sprint.requirementIds.includes(id))
    if (newReqIds.length === 0) return

    // Batch update requirements
    const batch = writeBatch(db)
    newReqIds.forEach((reqId) => {
      batch.update(doc(db, 'requirements', reqId), {
        sprintId,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })
    })

    // Update sprint document
    const updatedReqIds = [...sprint.requirementIds, ...newReqIds]
    batch.update(doc(db, 'sprints', sprintId), {
      requirementIds: updatedReqIds,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    await batch.commit()

    // Update local requirements state
    newReqIds.forEach((reqId) => {
      const req = requirementsStore.requirements.find((r) => r.id === reqId)
      if (req) {
        req.sprintId = sprintId
      }
    })

    // Update local sprint state
    const index = state.sprints.findIndex((s) => s.id === sprintId)
    if (index !== -1) {
      state.sprints[index] = {
        ...state.sprints[index],
        requirementIds: updatedReqIds,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }
    }

    // Recalculate and persist metrics
    await recalculateSprintMetrics(sprintId)
  }

  const removeRequirement = async (reqId: string, sprintId: string): Promise<void> => {
    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) throw new Error('Sprint no encontrado')

    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'
    const requirementsStore = useRequirementsStore()

    const batch = writeBatch(db)

    // Clear sprintId from requirement
    batch.update(doc(db, 'requirements', reqId), {
      sprintId: null,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    // Remove from sprint
    const updatedReqIds = sprint.requirementIds.filter((id) => id !== reqId)
    batch.update(doc(db, 'sprints', sprintId), {
      requirementIds: updatedReqIds,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    await batch.commit()

    // Update local state
    const req = requirementsStore.requirements.find((r) => r.id === reqId)
    if (req) {
      req.sprintId = undefined
    }

    const index = state.sprints.findIndex((s) => s.id === sprintId)
    if (index !== -1) {
      state.sprints[index] = {
        ...state.sprints[index],
        requirementIds: updatedReqIds,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }
    }

    // Recalculate and persist metrics
    await recalculateSprintMetrics(sprintId)
  }

  const updateRequirementDevStatus = async (
    reqId: string,
    status: DevelopmentStatus
  ): Promise<void> => {
    const requirementsStore = useRequirementsStore()
    await requirementsStore.updateDevelopmentStatus(reqId, status)

    // Recalculate sprint metrics if requirement is in a sprint
    const req = requirementsStore.getRequirementById(reqId)
    if (req?.sprintId) {
      await recalculateSprintMetrics(req.sprintId)
    }
  }

  const calculateSprintMetrics = (sprintId: string) => {
    const requirementsStore = useRequirementsStore()
    const sprint = state.sprints.find((s) => s.id === sprintId)
    if (!sprint) return { totalStoryPoints: 0, completedStoryPoints: 0, totalRequirements: 0, completedRequirements: 0 }

    const sprintReqs = sprint.requirementIds
      .map((id) => requirementsStore.getRequirementById(id))
      .filter(Boolean)

    const totalStoryPoints = sprintReqs.reduce((sum, r) => sum + (r!.storyPoints || 0), 0)
    const completedStoryPoints = sprintReqs
      .filter((r) => r!.developmentStatus === 'production')
      .reduce((sum, r) => sum + (r!.storyPoints || 0), 0)
    const totalRequirements = sprintReqs.length
    const completedRequirements = sprintReqs.filter((r) => r!.developmentStatus === 'production').length

    return { totalStoryPoints, completedStoryPoints, totalRequirements, completedRequirements }
  }

  const recalculateSprintMetrics = async (sprintId: string): Promise<void> => {
    const metrics = calculateSprintMetrics(sprintId)
    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'

    await updateDoc(doc(db, 'sprints', sprintId), {
      ...metrics,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserId,
    })

    const index = state.sprints.findIndex((s) => s.id === sprintId)
    if (index !== -1) {
      state.sprints[index] = {
        ...state.sprints[index],
        ...metrics,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }
    }
  }

  const updateRequirementBoardOrder = async (
    reqIds: string[],
    status: DevelopmentStatus
  ): Promise<void> => {
    const authStore = useAuthStore()
    const currentUserId = authStore.userId || 'system'
    const requirementsStore = useRequirementsStore()

    const batch = writeBatch(db)
    reqIds.forEach((reqId, index) => {
      batch.update(doc(db, 'requirements', reqId), {
        boardOrder: index,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })
    })
    await batch.commit()

    // Update local state
    reqIds.forEach((reqId, index) => {
      const req = requirementsStore.requirements.find((r) => r.id === reqId)
      if (req) {
        req.boardOrder = index
      }
    })
  }

  const setFilters = (filters: Partial<BacklogFilters>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const setSelectedSprint = (sprintId: string | null): void => {
    state.selectedSprintId = sprintId
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchSprints,
    createSprint,
    updateSprint,
    startSprint,
    completeSprint,
    cancelSprint,
    assignRequirements,
    removeRequirement,
    updateRequirementDevStatus,
    updateRequirementBoardOrder,
    recalculateSprintMetrics,
    setFilters,
    setSelectedSprint,
  }
}
